import React from 'react'

interface FunnelData {
  leadsGerados: number
  leadsAgendados: number
  comparecimento: number
  vendas: number
  investimento: number
  faturamento: number
  custoLead: number
  custoAgendamento: number
  custoComparecimento: number
  cac: number
  roi: number
  ticketMedio: number
}

interface FunnelChartProps {
  data: FunnelData
  periodo: string
}

export default function FunnelChart({ data, periodo }: FunnelChartProps) {
  // Calcular percentuais
  const taxaLeadAgendado = data.leadsGerados > 0 
    ? ((data.leadsAgendados / data.leadsGerados) * 100).toFixed(1)
    : '0.0'
  
  const taxaAgendadoComparecimento = data.leadsAgendados > 0
    ? ((data.comparecimento / data.leadsAgendados) * 100).toFixed(1)
    : '0.0'
  
  const taxaComparecimentoVenda = data.comparecimento > 0
    ? ((data.vendas / data.comparecimento) * 100).toFixed(1)
    : '0.0'

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 shadow-2xl">
      {/* Título */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          {periodo}
        </h2>
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20">
          <span className="text-white/70 text-xs font-medium">Budget Total:</span>
          <span className="text-white font-bold text-lg">{formatCurrency(data.investimento)}</span>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Funil com formato cônico real */}
        <div className="flex-1 flex flex-col items-center justify-center relative">
          <div className="relative w-full max-w-xl">
            {/* Leads Gerados - Topo do funil */}
            <div className="relative mb-1">
              <div 
                className="relative overflow-hidden"
                style={{
                  clipPath: 'polygon(0% 0%, 100% 0%, 95% 100%, 5% 100%)',
                  background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                  boxShadow: '0 10px 40px rgba(245, 158, 11, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                }}
              >
                <div className="px-12 py-8 text-center relative z-10">
                  <div className="flex items-center justify-between mb-2 px-6">
                    <span className="text-gray-900 font-bold text-xs uppercase tracking-wide">Leads Gerados</span>
                    <span className="bg-gray-900 text-amber-400 px-2 py-0.5 rounded-full text-xs font-bold shadow-lg">100%</span>
                  </div>
                  <div className="text-4xl font-black text-gray-900 drop-shadow-md">{formatNumber(data.leadsGerados)}</div>
                </div>
                {/* Efeito de brilho */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
              </div>
              {/* Seta de conexão */}
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 z-20">
                <svg className="w-8 h-8 text-amber-400 drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Leads Agendados */}
            <div className="relative mb-1 px-8">
              <div 
                className="relative overflow-hidden"
                style={{
                  clipPath: 'polygon(5% 0%, 95% 0%, 88% 100%, 12% 100%)',
                  background: 'linear-gradient(135deg, #F472B6 0%, #EC4899 100%)',
                  boxShadow: '0 10px 40px rgba(236, 72, 153, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                }}
              >
                <div className="px-12 py-7 text-center relative z-10">
                  <div className="flex items-center justify-between mb-2 px-6">
                    <span className="text-white font-bold text-xs uppercase tracking-wide">Leads Agendados</span>
                    <span className="bg-white text-pink-600 px-2 py-0.5 rounded-full text-xs font-bold shadow-lg">{taxaLeadAgendado}%</span>
                  </div>
                  <div className="text-3xl font-black text-white drop-shadow-md">{formatNumber(data.leadsAgendados)}</div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
              </div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 z-20">
                <svg className="w-8 h-8 text-pink-400 drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Comparecimento */}
            <div className="relative mb-1 px-20">
              <div 
                className="relative overflow-hidden"
                style={{
                  clipPath: 'polygon(12% 0%, 88% 0%, 78% 100%, 22% 100%)',
                  background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                  boxShadow: '0 10px 40px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                }}
              >
                <div className="px-12 py-7 text-center relative z-10">
                  <div className="flex items-center justify-between mb-2 px-6">
                    <span className="text-white font-bold text-xs uppercase tracking-wide">Comparecimento</span>
                    <span className="bg-white text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold shadow-lg">{taxaAgendadoComparecimento}%</span>
                  </div>
                  <div className="text-3xl font-black text-white drop-shadow-md">{formatNumber(data.comparecimento)}</div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
              </div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 z-20">
                <svg className="w-8 h-8 text-blue-400 drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            {/* Vendas - Base do funil */}
            <div className="relative px-32">
              <div 
                className="relative overflow-hidden"
                style={{
                  clipPath: 'polygon(22% 0%, 78% 0%, 70% 100%, 30% 100%)',
                  background: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
                  boxShadow: '0 10px 40px rgba(139, 92, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                }}
              >
                <div className="px-12 py-8 text-center relative z-10">
                  <div className="flex items-center justify-between mb-2 px-6">
                    <span className="text-white font-bold text-xs uppercase tracking-wide">Converteram</span>
                    <span className="bg-white text-violet-600 px-2 py-0.5 rounded-full text-xs font-bold shadow-lg">{taxaComparecimentoVenda}%</span>
                  </div>
                  <div className="text-2xl font-black text-white drop-shadow-md">{formatNumber(data.vendas)}</div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Ícone de sucesso */}
            <div className="flex justify-center mt-4">
              <div className="bg-gradient-to-r from-amber-400 to-amber-500 p-3 rounded-full shadow-2xl transform hover:scale-110 transition-transform">
                <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            {/* Total Faturado */}
            <div className="mt-4 text-center">
              <div className="text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">Total Faturado</div>
              <div className="text-3xl font-black text-white drop-shadow-lg">{formatCurrency(data.faturamento)}</div>
            </div>
          </div>
        </div>

        {/* Métricas Laterais */}
        <div className="w-64 flex flex-col gap-2">
          {/* Custo por Lead */}
          <div className="bg-white/5 backdrop-blur-md rounded-lg p-3 border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:border-amber-400/50">
            <div className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-0.5">Custo por Lead</div>
            <div className="text-xl font-bold text-amber-400">{formatCurrency(data.custoLead)}</div>
          </div>

          {/* Custo por Agendamento */}
          <div className="bg-white/5 backdrop-blur-md rounded-lg p-3 border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:border-pink-400/50">
            <div className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-0.5">Custo por Agendamento</div>
            <div className="text-xl font-bold text-pink-400">{formatCurrency(data.custoAgendamento)}</div>
          </div>

          {/* Custo por Comparecimento */}
          <div className="bg-white/5 backdrop-blur-md rounded-lg p-3 border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:border-blue-400/50">
            <div className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-0.5">Custo por Comparecimento</div>
            <div className="text-xl font-bold text-blue-400">{formatCurrency(data.custoComparecimento)}</div>
          </div>

          {/* CAC */}
          <div className="bg-white/5 backdrop-blur-md rounded-lg p-3 border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:border-violet-400/50">
            <div className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-0.5">CAC (Custo por Cliente)</div>
            <div className="text-xl font-bold text-violet-400">{formatCurrency(data.cac)}</div>
          </div>

          {/* ROI - Destaque */}
          <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-4 shadow-2xl hover:scale-105 transition-all border-2 border-emerald-400/50">
            <div className="text-white/90 text-xs uppercase tracking-wide font-bold mb-0.5">ROI</div>
            <div className="text-3xl font-black text-white drop-shadow-lg">{data.roi.toFixed(2)}</div>
          </div>

          {/* Ticket Médio */}
          <div className="bg-white/5 backdrop-blur-md rounded-lg p-3 border border-white/10 hover:bg-white/10 transition-all hover:scale-105 hover:border-cyan-400/50">
            <div className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-0.5">Ticket Médio</div>
            <div className="text-xl font-bold text-cyan-400">{formatCurrency(data.ticketMedio)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
